import { LIMITE_ANEXOS_NOTA, TipoAlvo, TipoNota, Visibilidade, type AlvoDto, type ConectorDto, type NotaDto } from '@nexital/shared';

// Post-its presos a grupos e cartões (RQ-NOT-003) e o selo de visibilidade. Funções puras.

export type Ponto = { x: number; y: number };
export type Retangulo = Ponto & { largura: number; altura: number };
/** Grupo ou cartão do quadro onde um post-it pode ficar preso. */
export type AlvoNoQuadro = Retangulo & { alvo: AlvoDto };

/** Prioridade quando se sobrepõem: o cartão (mais específico) vence o grupo. */
const prioridade = (a: AlvoNoQuadro) => (a.alvo.tipo === TipoAlvo.Processo ? 0 : 1);

const contem = (r: Retangulo, p: Ponto) => p.x >= r.x && p.x <= r.x + r.largura && p.y >= r.y && p.y <= r.y + r.altura;

/**
 * Onde o post-it largado em `ponto` (posição absoluta, canto superior esquerdo) fica:
 * preso ao cartão ou grupo por baixo, com a posição relativa a ele; senão solto onde caiu.
 */
export function soltarPostIt(ponto: Ponto, alvos: AlvoNoQuadro[]): { presoA: AlvoDto | null; posicao: Ponto } {
  const baixo = alvos.filter((a) => contem(a, ponto)).sort((a, b) => prioridade(a) - prioridade(b))[0];
  if (!baixo) return { presoA: null, posicao: ponto };
  return { presoA: baixo.alvo, posicao: { x: ponto.x - baixo.x, y: ponto.y - baixo.y } };
}

/** Posição absoluta de uma nota: presa, soma à do alvo; alvo sumido, fica na posição guardada. */
export function posicaoAbsoluta(nota: Pick<NotaDto, 'x' | 'y' | 'presoA'>, posicaoDoAlvo: (alvo: AlvoDto) => Ponto | null): Ponto {
  const propria = { x: nota.x ?? 0, y: nota.y ?? 0 };
  const base = nota.presoA && posicaoDoAlvo(nota.presoA);
  return base ? { x: base.x + propria.x, y: base.y + propria.y } : propria;
}

/** Quem não é o dono só move (RQ-NOT-010): a nota segue presa ao mesmo alvo, com a posição relativa a ele. */
export function posicaoSemTrocarAlvo(ponto: Ponto, presoA: AlvoDto | null, posicaoDoAlvo: (alvo: AlvoDto) => Ponto | null): Ponto {
  const base = presoA && posicaoDoAlvo(presoA);
  return base ? { x: ponto.x - base.x, y: ponto.y - base.y } : ponto;
}

/**
 * Selo mostrado na nota; nota do processo é sempre pública (RQ-NOT-002) e pública não tem selo. A restrita avisa
 * quem acima do autor pode acompanhá-la; a de outra pessoa, vista por acompanhamento, diz isso.
 */
export function seloVisibilidade(nota: Pick<NotaDto, 'tipo' | 'visibilidade' | 'acompanhada' | 'vistaPorSuperiores'>): string | null {
  if (nota.acompanhada) return 'acompanhando';
  if (nota.tipo === TipoNota.DoProcesso || nota.visibilidade === Visibilidade.Publica) return null;
  const tipo = nota.visibilidade === Visibilidade.Privada ? 'privada' : 'compartilhada';
  return nota.vistaPorSuperiores ? `${tipo} · ${nota.vistaPorSuperiores} podem ver` : tipo;
}

/**
 * O cadeado alterna entre pública e não pública (RQ-NOT-007). Ao fechar, a nota volta a ser compartilhada se ainda
 * há com quem (o compartilhamento fica guardado enquanto ela é pública); senão, privada.
 */
export function visibilidadePeloCadeado(nota: Pick<NotaDto, 'visibilidade' | 'compartilhadaCom'>): Visibilidade {
  if (nota.visibilidade !== Visibilidade.Publica) return Visibilidade.Publica;
  return nota.compartilhadaCom.length ? Visibilidade.Compartilhada : Visibilidade.Privada;
}

/** Compartilhar não muda a nota pública; nas outras, sem ninguém marcado ela volta a ser privada. */
export function visibilidadeAoCompartilhar(atual: Visibilidade, pessoas: number): Visibilidade {
  if (atual === Visibilidade.Publica) return atual;
  return pessoas ? Visibilidade.Compartilhada : Visibilidade.Privada;
}

/** Post-it ligado a um cartão (RQ-NOT-008): seta para um cartão, em qualquer sentido, ou dentro de uma licitação. */
export function ligadaACartao(notaUuid: string, setas: ConectorDto[], dentroDaLicitacao: boolean): boolean {
  if (dentroDaLicitacao) return true;
  const ponta = (a: AlvoDto, b: AlvoDto) => a.uuid === notaUuid && b.tipo === TipoAlvo.Processo;
  return setas.some((s) => ponta(s.origem, s.destino) || ponta(s.destino, s.origem));
}

export const MENSAGEM_SETA_SEM_POST_IT = 'A seta precisa ter um post-it em uma das pontas.';
/** Post-it de outra pessoa só se move (RQ-NOT-010). */
export const MENSAGEM_SO_O_DONO_EXCLUI = 'Só quem criou o post-it pode excluí-lo.';

/** Toda seta tem um post-it em pelo menos uma das pontas (RQ-NOT-004). */
export function setaPermitida(origem: AlvoDto, destino: AlvoDto): boolean {
  return origem.tipo === TipoAlvo.Nota || destino.tipo === TipoAlvo.Nota;
}

/** No máximo LIMITE_ANEXOS_NOTA anexos por post-it (RQ-NOT-009). */
export function podeAnexar(nota: Pick<NotaDto, 'anexos'>): boolean {
  return nota.anexos.length < LIMITE_ANEXOS_NOTA;
}
